import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import type { PostFields } from '@buffer-mono/reminders-config'

import type { Service } from '~publish/components/Composer/graphqlEnums'
import {
  AttachmentTypes,
  SERVICE_FACEBOOK,
  SERVICE_GOOGLEBUSINESS,
  SERVICE_INSTAGRAM,
  SERVICE_LINKEDIN,
  SERVICE_MASTODON,
  SERVICE_STARTPAGE,
  SERVICE_THREADS,
  SERVICE_TIKTOK,
  SERVICE_TWITTER,
  SERVICE_YOUTUBE,
  Services,
} from '~publish/legacy/constants'
import type { ServiceDefinition } from '~publish/legacy/constants/services/types'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'
import {
  PostTypeFacebookReel,
  PostTypeFacebookStory,
  PostTypePost,
  PostTypeReel,
  PostTypeShort,
  PostTypeStory,
} from '~publish/legacy/post/constants'
import { VALIDATION_CODE } from '~publish/legacy/validation/constants'

import { DraftMethods } from './DraftMethods'
import type { ChannelData, ChannelDataProperties } from './types'
import * as assetListOps from '~publish/components/Composer/assets/assetListOps'
import type { DraftAsset } from '~publish/components/Composer/assets/assetListOps'
import { getYoutubeDefaultsFromStorage } from '../../components/youtube/utils'
import countHashtagsInText from '../../lib/validation/HashtagCounter'
import * as ValidatorFactory from '../../lib/validation/ValidatorFactory'
import { MENTION_REGEX } from '../../utils/custom-plugins/mention'
import { Document } from '../Document'
import type { DocumentTitle } from '../Document'
import { EditorStateProxy } from '../EditorStateProxy/EditorStateProxy'
import type { Gif, Image, Link, Retweet, SourceLink, Video } from '../factories'

dayjs.extend(utc)
dayjs.extend(timezone)

export type Post = {
  text: string
  characterCount: number
  enabledAttachmentType: string | null
  link: Link | null
  images: Image[]
  /** The first attached video, kept for compatibility — see `videos` for every attached video, in order. */
  video: Video | null
  videos: Video[]
  gif: Gif | null
  retweet: Retweet | null
  urls: string[]
  unshortenedUrls: string[]
}
export type Thread = Post[] | null
export type RequiredThread = { thread: NonNullable<Thread> }

export class Draft {
  /**
   * Unique identifier; currently that's the service name
   */
  id: 'omni' | Service

  /**
   * Type to differentiate between different update types in one channel
   * (e.g. reel or post for IG).
   * The mobile apps team uses a `post_type` property that is similar,
   * but Publish doesn't know anything about this field. Instead,
   * Publish relies on `updateType`.
   */
  updateType: string | null = null

  /**
   * Service data structure in AppConstants.js
   */
  service: ServiceDefinition

  editorState: BufferEditor

  /**
   * Text content of the post
   */
  text = ''

  /**
   * Urls contained in the text
   * @type {string[]}
   */
  urls: string[] = []

  /**
   * Urls unshortened by user
   * @type {string[]}
   */
  unshortenedUrls: string[] = []

  /**
   * Link Attachment; data structure in getNewLink()
   */
  link: Link | null = null

  /**
   * Media Attachment (type: image); data structure in getNewImage(). Backed by
   * `mediaAssets`; see the accessor installed in the constructor.
   */
  declare images: Image[]

  availableImages: Image[] = []

  /**
   * Media Attachment (type: video) — the first attached video, kept as a
   * single value for compatibility with every existing consumer. See
   * `videos` for the full ordered list on a channel whose
   * `MixedMediaCapability.canHaveMultipleVideos` allows more than one. Data
   * structure in getNewVideo(). Backed by `mediaAssets`; see the accessor
   * installed in the constructor.
   */
  declare video: Video | null

  /**
   * Every attached video, in order. Most channels hold at most one; a
   * channel whose `MixedMediaCapability.canHaveMultipleVideos` is true can
   * hold more via `appendVideo`. Backed by `mediaAssets`; see the accessor
   * installed in the constructor.
   */
  declare videos: Video[]

  /**
   * Reference to the media object being actively edited
   */
  attachedMediaEditingPayload = null

  /**
   * Media Attachment (type: gif); data structure in getNewGif(). Backed by
   * `mediaAssets`; see the accessor installed in the constructor.
   */
  declare gif: Gif | null

  /**
   * Media Attachment (type: document). Backed by `mediaAssets`; see the
   * accessor installed in the constructor.
   */
  declare document: Document | DocumentTitle | null

  /**
   * Read-only snapshot of the ordered media asset list backing
   * images/video/gif/document. Backed by `mediaAssets`; see the accessor
   * installed in the constructor.
   */
  declare assets: readonly DraftAsset[]

  /**
   * Single ordered source of truth for images/video/gif/document, which are
   * derived accessor properties (see the constructor). Order across kinds
   * follows the `image, video, document, gif` convention shared with
   * `draftMediaToAssets` in mapDraftToOmni.ts.
   */
  private mediaAssets: DraftAsset[] = []

  /**
   * Bumped by every media mutation funnel method; lets `images` cache its
   * derived array and only rebuild once the underlying assets actually change.
   */
  private mediaVersion = 0

  private imagesCache: { version: number; value: Image[] } | null = null

  /**
   * Data structure in getNewRetweet();
   */
  retweet: Retweet | null = null

  get characterCount(): number {
    return DraftMethods.getFullCharacterCount(this, this.text)
  }

  /**
   * Only updated for services w/ comment char limit
   * @type {number|null}
   */
  characterCommentCount: number | null = null

  /**
   * Only updated for services w/ title char limit
   * @type {number|null}
   */
  characterTitleCount: number | null = null

  isEnabled = false

  /**
   * @type {AttachmentTypes}
   */
  enabledAttachmentType: string | null = null

  /**
   * Source url and page metadata; data structure in getNewSourceLink()
   */
  sourceLink: SourceLink | null = null

  isSaved = false

  hasSavingError = false

  aiAssisted = false

  shortLinkLongLinkMap = new Map()

  scheduledAt: number | null = null

  /**
   * null when scheduledAt is null; true/false otherwise
   */
  isPinnedToSlot: boolean | null = null

  isTaggingPageLocation = null

  title: string | null = null

  /**
   * Object holding data for channel-specific fields (e.g. GBP)
   */
  channelData: ChannelData | null = null

  places: unknown[] | null = null

  composerSidebarVisible = false

  isReminder?: boolean

  isTwitterPremium = false

  mastodonMaxCharacters: number | undefined = undefined

  // Should we shorten links automatically in the text using bit.ly
  shortenLinksToggle = false

  // When a message is too short to be shortened, we show a message
  // to the user. This flag is used to control the visibility of that message.
  showShortLinkMessage = false

  /**
   * Used to store what stickers are selected for reminder fields
   */
  selectedStickers: PostFields[] = []

  thread: Thread = null

  /**
   * Tags selected for the post
   */
  tags: string[] = []

  /**
   * Set of image URLs currently being replaced in this draft
   * Used to show loading overlay only on the specific channel being edited
   */
  imagesBeingReplaced: Set<string> = new Set()

  constructor(service: ServiceDefinition, editorState: BufferEditor) {
    this.id = service.name
    this.service = service
    this.editorState = editorState
    this.text = EditorStateProxy.getPlainText(editorState)
    this.defineMediaAccessors()

    this.characterCommentCount = service.commentCharLimit === null ? null : 0
    this.characterTitleCount = service.titleCharLimit === null ? null : 0
    this.isTwitterPremium = false
    this.mastodonMaxCharacters = undefined

    if (service.isThreads()) {
      this.shortenLinksToggle = false
    }

    if (service.isGoogleBusiness()) {
      this.updateType = 'whats_new'
      this.channelData = {
        googlebusiness: {
          start_date: dayjs.utc().startOf('day').unix(),
          end_date: dayjs.utc().add(7, 'day').startOf('day').unix(),
        },
      }
    }

    if (service.isInstagram()) {
      this.updateType = PostTypePost
      this.channelData = {
        instagram: {
          share_to_feed: true,
        },
      }
    }

    if (service.isStartPage()) {
      this.channelData = {
        startPage: {},
      }
    }

    if (service.isYoutube()) {
      const defaultChannelData = getYoutubeDefaultsFromStorage()
      this.updateType = PostTypeShort
      this.channelData = {
        youtube: defaultChannelData,
      }
    }

    if (service.hasPostTypes()) {
      this.updateType = service.getDefaultPostType()
    }

    if (service.canHaveAttachmentType(AttachmentTypes.MEDIA, this.updateType)) {
      this.enabledAttachmentType = AttachmentTypes.MEDIA
    } else {
      this.enabledAttachmentType = null
    }
  }

  /**
   * Installs images/video/gif/document as own enumerable accessor properties
   * (rather than prototype getters), so that `{...draft}` spreads and
   * `Object.keys`/`Object.assign` enumerate their current derived values
   * exactly like ordinary data fields. Setters are a thin compat shim for
   * existing call sites and fixtures that assign these fields directly; they
   * delegate to the same mutation-funnel methods as everything else.
   *
   * The backing fields are made non-enumerable so that shallow copies and
   * rest-destructures of a draft only carry the derived media fields — never
   * the live asset list or its cache bookkeeping.
   */
  private defineMediaAccessors(): void {
    for (const key of ['mediaAssets', 'mediaVersion', 'imagesCache'] as const) {
      Object.defineProperty(this, key, { enumerable: false })
    }

    Object.defineProperty(this, 'images', {
      enumerable: true,
      configurable: true,
      get: (): Image[] => this.getImages(),
      set: (images: Image[]): void => this.setImages(images),
    })

    Object.defineProperty(this, 'video', {
      enumerable: true,
      configurable: true,
      get: (): Video | null =>
        assetListOps.firstOfKind(this.mediaAssets, 'video')?.video ?? null,
      set: (video: Video | null): void => this.setVideo(video),
    })

    Object.defineProperty(this, 'videos', {
      enumerable: true,
      configurable: true,
      get: (): Video[] => assetListOps.videosOf(this.mediaAssets),
    })

    Object.defineProperty(this, 'gif', {
      enumerable: true,
      configurable: true,
      get: (): Gif | null =>
        assetListOps.firstOfKind(this.mediaAssets, 'gif')?.gif ?? null,
      set: (gif: Gif | null): void => this.setGif(gif),
    })

    Object.defineProperty(this, 'document', {
      enumerable: true,
      configurable: true,
      get: (): Document | DocumentTitle | null =>
        assetListOps.firstOfKind(this.mediaAssets, 'document')?.document ??
        null,
      set: (document: Document | DocumentTitle | null): void =>
        this.setDocument(document),
    })

    Object.defineProperty(this, 'assets', {
      enumerable: true,
      configurable: true,
      get: (): readonly DraftAsset[] => [...this.mediaAssets],
    })
  }

  /**
   * Derives the ordered image list from `mediaAssets`, caching it by
   * `mediaVersion` so repeated reads between mutations return the same array
   * reference (relied on by `useMemo` deps that key off `draft.images`).
   */
  private getImages(): Image[] {
    if (this.imagesCache && this.imagesCache.version === this.mediaVersion) {
      return this.imagesCache.value
    }
    const value = assetListOps.imagesOf(this.mediaAssets)
    this.imagesCache = { version: this.mediaVersion, value }
    return value
  }

  isEnabledOrOmni(): boolean {
    return this.isEnabled || this.service.isOmni
  }

  isEmpty(): boolean {
    return (
      this.isTextEmpty() &&
      (this.enabledAttachmentType !== AttachmentTypes.LINK ||
        this.link === null) &&
      (this.canHaveMedia() || this.images.length === 0) &&
      (this.canHaveMedia() || this.video === null) &&
      (this.canHaveMedia() || this.gif === null) &&
      (this.canHaveMedia() || this.document === null) &&
      (this.enabledAttachmentType !== AttachmentTypes.RETWEET ||
        this.retweet === null) &&
      this.sourceLink === null &&
      !this.title &&
      !this.hasThreadContent() &&
      !this.hasChannelDataContent()
    )
  }

  hasThreadContent(): boolean {
    if (!this.hasThread()) return false
    return this.thread.some(
      (post) =>
        post.text.length > 0 ||
        post.images.length > 0 ||
        post.video !== null ||
        post.gif !== null ||
        post.link !== null,
    )
  }

  hasChannelDataContent(): boolean {
    const data = this.channelData
    if (!data) return false
    return !!(
      data.instagram?.comment_text ||
      data.linkedin?.comment_text ||
      data.facebook?.comment_text ||
      data.mastodon?.spoilerText ||
      data.googlebusiness?.title ||
      data.googlebusiness?.link ||
      data.tiktok?.title ||
      data.threads?.topic
    )
  }

  hasLinkAttachmentEnabled(): boolean {
    return DraftMethods.hasLinkAttachmentEnabled(this)
  }

  hasMediaAttachmentEnabled(): boolean {
    return DraftMethods.hasMediaAttachmentEnabled(this)
  }

  hasRetweetAttachmentEnabled(): boolean {
    return DraftMethods.hasRetweetAttachmentEnabled(this)
  }

  hasLinkAttachment(): boolean {
    return DraftMethods.hasLinkAttachment(this)
  }

  hasLinkAttachmentWithThumbnail(): boolean {
    return DraftMethods.hasLinkAttachmentWithThumbnail(this)
  }

  canEditLinkAttachment(
    selectedProfiles: Array<{
      id: string
      serviceType: string
      serviceName: string
    }>,
    domainsOwnedByFacebookPages?: Map<string, Map<string, boolean>>,
  ): {
    canEdit: boolean
    isResultCertain: boolean
    hasNonPages?: boolean
    isLoading: boolean
  } {
    return DraftMethods.canEditLinkAttachment(
      this,
      selectedProfiles,
      domainsOwnedByFacebookPages,
    )
  }

  hasMediaAttachment(): boolean {
    return DraftMethods.hasMediaAttachment(this)
  }

  hasRetweetAttachment(): boolean {
    return DraftMethods.hasRetweetAttachment(this)
  }

  hasAttachment(): boolean {
    return DraftMethods.hasAttachment(this)
  }

  hasNoMediaAttached(): boolean {
    return DraftMethods.hasNoMediaAttached(this)
  }

  hasVideoAttachment(): boolean {
    return DraftMethods.hasVideoAttachment(this)
  }

  hasImagesAttachment(): boolean {
    return DraftMethods.hasImagesAttachment(this)
  }

  isReelsPost(): boolean {
    return this.service.isInstagram() && this.updateType === PostTypeReel
  }

  isFacebookReelPost(): boolean {
    return this.service.isFacebook() && this.updateType === PostTypeFacebookReel
  }

  isFacebookStoryPost(): boolean {
    return (
      this.service.isFacebook() && this.updateType === PostTypeFacebookStory
    )
  }

  isStoryPost(): boolean {
    return this.service.isInstagram() && this.updateType === PostTypeStory
  }

  isShortPost(): boolean {
    return this.service.isYoutube() && this.updateType === PostTypeShort
  }

  hasGifAttachment(): boolean {
    return this.hasMediaAttachmentEnabled() && this.gif !== null
  }

  canAttachMoreImages(): boolean {
    return (
      this.service.canHaveAttachmentType(
        AttachmentTypes.MEDIA,
        this.updateType,
      ) &&
      this.hasMediaAttachmentEnabled() &&
      // if we have a document, no images can be attached
      !Document.isDocument(this.document) &&
      this.service.maxAttachableImagesCount(this) > this.images.length
    )
  }

  canAttachMoreMedia(
    capability: { canMixMediaTypes: boolean } = { canMixMediaTypes: false },
  ): boolean {
    const otherKindBlocksAttachment =
      !capability.canMixMediaTypes &&
      (this.hasVideoAttachment() || this.hasGifAttachment())
    const isStoryReminder =
      this.service.isInstagram() && this.isStoryPost() && this.isReminder
    const hasRoom =
      !isStoryReminder ||
      DraftMethods.getFileAttachmentCount(this) <
        DraftMethods.getMaxAttachableAssetsCount(this)
    return this.canAttachMoreImages() && !otherKindBlocksAttachment && hasRoom
  }

  getTotalImageUploadsRemaining(): number {
    const maxImages = this.service.maxAttachableImagesCount(this)
    const attachedImages = this.images?.length || 0
    return maxImages - attachedImages
  }

  getImageByUrl(url: string): Image | null {
    if (this.hasImagesAttachment()) {
      return this.images.find((image) => image.url === url) || null
    }

    return null
  }

  /**
   * Media mutation funnel: these methods — and the images/video/gif/document
   * accessor setters, which delegate to them — are the only place the
   * draft's ordered media asset list (`mediaAssets`) may be restructured.
   * Direct assignment/splice on the derived fields outside this group is
   * not allowed.
   */
  setImages(images: Image[]): void {
    this.mediaAssets = assetListOps.setImages(this.mediaAssets, images)
    this.touchMedia()
    // Alias the given array directly rather than rebuilding it from
    // mediaAssets, matching the legacy field-assignment semantics that
    // `expect(draft.images).toBe(nextImages)`-style fixtures rely on.
    this.imagesCache = { version: this.mediaVersion, value: images }
  }

  insertImageAt(index: number, image: Image): void {
    this.mediaAssets = assetListOps.insertImageAt(
      this.mediaAssets,
      index,
      image,
    )
    this.touchMedia()
  }

  appendImage(image: Image): void {
    this.mediaAssets = assetListOps.appendImage(this.mediaAssets, image)
    this.touchMedia()
  }

  replaceImageAt(index: number, image: Image): void {
    this.mediaAssets = assetListOps.replaceImageAt(
      this.mediaAssets,
      index,
      image,
    )
    this.touchMedia()
  }

  removeImageAt(index: number): void {
    this.mediaAssets = assetListOps.removeImageAt(this.mediaAssets, index)
    this.touchMedia()
  }

  /**
   * Drops up to `overflow` images from the end of the list, e.g. to make
   * room for newly attached images once the service's attachment limit
   * is exceeded.
   */
  truncateImages(overflow: number): void {
    if (overflow <= 0) return
    this.mediaAssets = assetListOps.truncateImages(this.mediaAssets, overflow)
    this.touchMedia()
  }

  /**
   * Moves the asset at `fromIndex` to `toIndex` across the whole ordered
   * list, regardless of kind — e.g. dragging a video between two images in
   * the tray. See `assetListOps.moveAsset` for why this doesn't restore
   * canonical kind order afterwards.
   */
  reorderAssetAt(fromIndex: number, toIndex: number): void {
    this.mediaAssets = assetListOps.moveAsset(
      this.mediaAssets,
      fromIndex,
      toIndex,
    )
    this.touchMedia()
  }

  clearImages(): void {
    this.mediaAssets = assetListOps.removeKind(this.mediaAssets, 'image')
    this.touchMedia()
  }

  setVideo(video: Video | null): void {
    this.mediaAssets = assetListOps.replaceKind(
      this.mediaAssets,
      'video',
      video ? { kind: 'video', video } : null,
    )
    this.touchMedia()
  }

  /**
   * Appends a video alongside any already attached, rather than replacing
   * them. Only valid to call where `MixedMediaCapability.canHaveMultipleVideos`
   * is true — every other draft attaches a video via `setVideo`.
   */
  appendVideo(video: Video): void {
    this.mediaAssets = assetListOps.appendVideo(this.mediaAssets, video)
    this.touchMedia()
  }

  /** Removes one video from a multi-video draft, leaving any others intact. */
  removeVideoAt(index: number): void {
    this.mediaAssets = assetListOps.removeVideoAt(this.mediaAssets, index)
    this.touchMedia()
  }

  setGif(gif: Gif | null): void {
    this.mediaAssets = assetListOps.replaceKind(
      this.mediaAssets,
      'gif',
      gif ? { kind: 'gif', gif } : null,
    )
    this.touchMedia()
  }

  setDocument(document: Document | DocumentTitle | null): void {
    this.mediaAssets = assetListOps.replaceKind(
      this.mediaAssets,
      'document',
      document ? { kind: 'document', document } : null,
    )
    this.touchMedia()
  }

  resetMedia(): void {
    this.mediaAssets = []
    this.touchMedia()
  }

  /**
   * Re-sequences the currently attached media to match `order`, without
   * adding or removing anything. The initial-load dispatch attaches images,
   * then video, then document in that fixed sequence regardless of true
   * attach order — this restores it once loading settles.
   */
  reorderMedia(order: readonly DraftAsset['kind'][]): void {
    const reordered = assetListOps.reorderByKindSequence(
      this.mediaAssets,
      order,
    )
    const isUnchanged =
      reordered.length === this.mediaAssets.length &&
      reordered.every((asset, index) => asset === this.mediaAssets[index])
    if (isUnchanged) return

    this.mediaAssets = reordered
    this.touchMedia()
  }

  /** Invalidates the `images` cache; called by every media funnel method. */
  private touchMedia(): void {
    this.mediaVersion++
  }

  hasText(): boolean {
    return DraftMethods.hasText(this)
  }

  hasRequiredAttachmentAttached(): boolean {
    return (
      this.service.requiredAttachmentType === null ||
      (this.service.requiredAttachmentType === AttachmentTypes.LINK &&
        this.hasLinkAttachment()) ||
      (this.service.requiredAttachmentType === AttachmentTypes.RETWEET &&
        this.hasRetweetAttachment()) ||
      (this.service.requiredAttachmentType === AttachmentTypes.MEDIA &&
        this.hasAttachment())
    )
  }

  hasMediaAttachmentSwitch(): boolean {
    return (
      !this.hasMediaAttachmentEnabled() &&
      this.service.canHaveSomeAttachmentType(
        [AttachmentTypes.LINK, AttachmentTypes.RETWEET],
        this.updateType,
      )
    )
  }

  hasLinkAttachmentSwitch(): boolean {
    return this.hasMediaAttachmentEnabled() && this.link !== null
  }

  hasRetweetAttachmentSwitch(): boolean {
    return this.hasMediaAttachmentEnabled() && this.retweet !== null
  }

  hasAttachmentSwitch(): boolean {
    return (
      this.hasMediaAttachmentSwitch() ||
      this.hasLinkAttachmentSwitch() ||
      this.hasRetweetAttachmentSwitch()
    )
  }

  canType(): boolean {
    return !(this.isStoryPost() || this.isFacebookStoryPost())
  }

  canTypeMoreCharacters(): boolean {
    const charLimit = DraftMethods.getCharLimit(this)

    return (
      charLimit !== null &&
      this.characterCount !== null &&
      (charLimit - this.characterCount <= 280 || !this.service.isFacebook())
    )
  }

  canTypeMoreCommentCharacters(): boolean {
    return (
      this.service.commentCharLimit !== null &&
      this.characterCommentCount !== null &&
      this.service.commentCharLimit - this.characterCommentCount <= 280
    )
  }

  isTextEmpty(): boolean {
    return this.text.length === 0
  }

  canHaveMedia(): boolean {
    return this.enabledAttachmentType !== AttachmentTypes.MEDIA
  }

  getNumberOfMentionsInText(): number {
    const captionText = this.text
    const matchesInCaption = captionText.match(MENTION_REGEX)

    return matchesInCaption !== null ? matchesInCaption.length : 0
  }

  getNumberOfMentionsInComment(): number {
    const commentText = this.getCommentText()
    const matchesInComment = commentText.match(MENTION_REGEX)
    return matchesInComment !== null ? matchesInComment.length : 0
  }

  getNumberOfMentions(): number {
    return (
      this.getNumberOfMentionsInText() + this.getNumberOfMentionsInComment()
    )
  }

  getNumberOfHashtagsInText(): number {
    return countHashtagsInText(this.text)
  }

  getCommentText(): string {
    const channelData = this.channelData?.[this.service.name]

    return typeof channelData === 'object' &&
      channelData !== null &&
      'comment_text' in channelData &&
      typeof channelData.comment_text === 'string'
      ? channelData.comment_text
      : ''
  }

  getNumberOfHashtagsInComment(): number {
    const commentText = this.getCommentText()
    return countHashtagsInText(commentText)
  }

  getNumberOfHashtags(): number {
    return (
      this.getNumberOfHashtagsInText() + this.getNumberOfHashtagsInComment()
    )
  }

  getTotalUserTagsCount(excludingImage?: Image): number {
    if (!this.hasImagesAttachment()) {
      return 0
    }

    return this.images.reduce((total, image) => {
      if (image === excludingImage) {
        return total
      }

      return total + (image.userTags?.length || 0)
    }, 0)
  }

  getAttachmentThumbnails(): string[] | null {
    let thumbnails: string[] | null = null

    if (this.hasVideoAttachment() && this.video !== null) {
      thumbnails = [this.video.thumbnail]
    } else if (
      this.hasGifAttachment() &&
      this.gif !== null &&
      this.gif.stillGifUrl !== null
    ) {
      thumbnails = [this.gif.stillGifUrl]
    } else if (this.hasImagesAttachment()) {
      thumbnails = this.images.map((image) => image.url)
    } else if (
      this.hasLinkAttachmentWithThumbnail() &&
      this.link !== null &&
      this.link.thumbnail !== null
    ) {
      thumbnails = [this.link.thumbnail.url]
    } else if (this.hasRetweetAttachment() && this.retweet !== null) {
      thumbnails = [this.retweet.avatarUrl]
    } else if (DraftMethods.hasDocumentAttachment(this)) {
      const thumb = DraftMethods.getDocumentAttachment(this)?.thumbnailUrl
      if (thumb) thumbnails = [thumb]
    }

    return thumbnails
  }

  isVerticalVideoOnlySupported(): boolean {
    const serviceConfig = Services.get(this.service.name)
    return (
      (serviceConfig && serviceConfig.onlyAllowsVerticalVideo) ||
      this.isReelsPost() ||
      this.isFacebookReelPost()
    )
  }

  isImageFirst(): boolean {
    return (
      this.service.usesImageFirstLayout &&
      (this.hasNoMediaAttached() || this.isVerticalVideoOnlySupported())
    )
  }

  canHaveChannelData(): boolean {
    return [
      SERVICE_GOOGLEBUSINESS,
      SERVICE_INSTAGRAM,
      SERVICE_STARTPAGE,
      SERVICE_YOUTUBE,
      SERVICE_LINKEDIN,
      SERVICE_MASTODON,
      SERVICE_TIKTOK,
      SERVICE_THREADS,
      SERVICE_FACEBOOK,
      SERVICE_TWITTER,
    ].includes(this.id)
  }

  setChannelData(channelData?: ChannelDataProperties | null): void {
    if (!this.canHaveChannelData()) {
      this.channelData = null
      return
    }

    this.channelData = channelData ? { [this.id]: channelData } : {}
  }

  canHaveThread(): boolean {
    return this.updateType === 'thread'
  }

  hasThread(): this is Draft & RequiredThread {
    return Array.isArray(this.thread) && this.thread.length > 1
  }

  isUnderThreadPostsCountLimit(): boolean {
    if (this.service.maxThreads && Array.isArray(this.thread)) {
      return this.thread.length < this.service.maxThreads
    } else {
      return true
    }
  }

  canAddToThread(activeThreadId: number): {
    hasErrors: boolean
    shouldJumpToNextThreadedPost: boolean
    errors: string[]
  } {
    const foundErrors = ValidatorFactory.validate(this, false, activeThreadId)

    const errorsForActiveThread = foundErrors.results.filter((error) => {
      return (
        error.metadata?.threadId === activeThreadId &&
        error.code === VALIDATION_CODE.MISSING_VALUE
      )
    })

    let foundErrorsForActiveThread = errorsForActiveThread.map(
      (error) => error.message,
    )

    if (!this.isUnderThreadPostsCountLimit()) {
      foundErrorsForActiveThread = [
        `Number of ${this.service.nameOfPost}s reached the limit of ${this.service.maxThreads}`,
      ]
    }

    const hasErrors = foundErrorsForActiveThread.length > 0
    const errorsForNextThread = foundErrors.results.filter((error) => {
      return (
        error.metadata?.threadId === activeThreadId + 1 &&
        error.code === VALIDATION_CODE.MISSING_VALUE
      )
    })

    return {
      errors: foundErrorsForActiveThread,
      hasErrors,
      shouldJumpToNextThreadedPost:
        !hasErrors && errorsForNextThread.length > 0,
    }
  }

  shouldShortenLinks(): boolean {
    return this.shortenLinksToggle && this.service.isShorteningLinksAvailable
  }

  shouldShowShortLinkMessage(): boolean {
    return this.shouldShortenLinks() && this.showShortLinkMessage
  }

  isStickerSelected(sticker: PostFields): boolean {
    return this.selectedStickers.includes(sticker)
  }
}
export interface SlateDraft extends Draft {
  editorState: BufferEditor
}

export default Draft
